<!--
  EntityPicker — friendly autocomplete for picking a User, Restaurant, or UserGroup
  by name/email instead of a raw Mongo ObjectId.

  Props:
  modelValue : selected entity object (or null); an array of entities when `multiple`
  type       : 'member' | 'restaurant' | 'group'
  label      : input label
  density    : passed through to VAutocomplete
  multiple   : allow selecting several entities (renders closable chips)

  Emits:
  update:modelValue (entity | null, or entity[] when `multiple`)
-->
<script setup>
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useUsersStore, useRestaurantsStore, useUserGroupsStore } from '@/stores'

const props = defineProps({
  modelValue: { type: Object, default: null },
  type: { type: String, required: true },
  label: { type: String, default: '' },
  density: { type: String, default: 'compact' },
  placeholder: { type: String, default: '' },
  multiple: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

const usersStore = useUsersStore()
const restaurantsStore = useRestaurantsStore()
const userGroupsStore = useUserGroupsStore()

const { users } = storeToRefs(usersStore)
const { restaurantSearchResults } = storeToRefs(restaurantsStore)
const { userGroups } = storeToRefs(userGroupsStore)

const computedLabel = computed(() => {
  if (props.label) return props.label
  if (props.type === 'member') return 'Member'
  if (props.type === 'restaurant') return 'Restaurant'
  if (props.type === 'group') return 'Group'

  return ''
})

const items = computed(() => {
  if (props.type === 'member') return users.value || []
  if (props.type === 'restaurant') return restaurantSearchResults.value || []
  if (props.type === 'group') return userGroups.value || []

  return []
})

// Empty value differs by mode: [] for multi-select, null for single.
const emptyValue = () => (props.multiple ? [] : null)

const selected = ref(props.modelValue || emptyValue())
watch(() => props.modelValue, val => { selected.value = val || emptyValue() })

const onChange = val => {
  const next = val || emptyValue()
  selected.value = next
  emit('update:modelValue', next)
}

const displayLine = item => {
  if (!item) return ''
  if (props.type === 'member') {
    const name = `${item.firstName || ''} ${item.lastName || ''}`.trim()

    return name ? `${name} — ${item.email || ''}` : (item.email || '')
  }
  if (props.type === 'restaurant') return `${item.name || ''}${item.market ? ` — ${item.market}` : ''}`
  if (props.type === 'group') return `${item.name || ''}${item.type ? ` (${item.type})` : ''}`

  return ''
}

let timer = null
const onSearch = term => {
  if (!term || term.length < 2) return
  clearTimeout(timer)
  timer = setTimeout(() => {
    if (props.type === 'member') usersStore.getSearchedUsers(term)
    else if (props.type === 'restaurant') restaurantsStore.searchRestaurants(term)
    else if (props.type === 'group' && (!userGroups.value || userGroups.value.length === 0)) {
      userGroupsStore.getAllUserGroups()
    }
  }, 300)
}

// For groups we filter client-side — load once on mount
import { onMounted } from 'vue'
onMounted(() => {
  if (props.type === 'group' && (!userGroups.value || userGroups.value.length === 0)) {
    userGroupsStore.getAllUserGroups()
  }
})

const iconFor = computed(() => {
  if (props.type === 'member') return 'mdi-account-search-outline'
  if (props.type === 'restaurant') return 'mdi-silverware-fork-knife'
  if (props.type === 'group') return 'mdi-account-group-outline'

  return 'mdi-magnify'
})

const noDataText = computed(() => {
  if (props.type === 'member') return 'Type 2+ characters to search members'
  if (props.type === 'restaurant') return 'Type 2+ characters to search restaurants'

  return 'No matches'
})
</script>

<template>
  <VAutocomplete
    :model-value="selected"
    :items="items"
    :item-title="displayLine"
    item-value="_id"
    return-object
    :multiple="multiple"
    :chips="multiple"
    :closable-chips="multiple"
    :label="computedLabel"
    :placeholder="placeholder"
    :density="density"
    hide-details
    clearable
    :no-data-text="noDataText"
    :prepend-inner-icon="iconFor"
    @update:model-value="onChange"
    @update:search="onSearch"
  >
    <template #item="{ props: itemProps, item }">
      <VListItem
        v-bind="itemProps"
        :title="undefined"
      >
        <template #default>
          <div class="d-flex flex-column">
            <span class="text-body-2 font-weight-medium">
              {{ displayLine(item.raw) }}
            </span>
            <span
              v-if="props.type === 'member' && item.raw?.company"
              class="text-caption text-medium-emphasis"
            >
              {{ item.raw.company }}{{ item.raw.title ? ` · ${item.raw.title}` : '' }}
            </span>
            <span
              v-if="props.type === 'restaurant' && item.raw?.status"
              class="text-caption text-medium-emphasis"
            >
              {{ item.raw.status }}
            </span>
          </div>
        </template>
      </VListItem>
    </template>
  </VAutocomplete>
</template>
